import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import { getQuizResultsByUser } from '@/services/quizGameService';
import { QuizResultSummary } from '@/models/quiz-game/types';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
// import { useAuthStore } from '@/stores/authStore';
import { FaClock, FaTrophy, FaCalendarAlt, FaEye, FaGamepad, FaCheckCircle } from 'react-icons/fa';
import { supabase } from '@/lib/supabase';
// import { formatDistanceToNow, formatDuration } from '@/utils/formatters';

// UI Components
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Trophy,
  History,
  ChevronRight,
  Clock,
  Award,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Activity,
  Loader2
} from 'lucide-react';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis, CustomPagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { format as formatDate, formatDistance } from 'date-fns';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner"; // Custom spinner component for shadcn
import { cn } from "@/lib/utils";

// Định nghĩa thay thế cho formatters
const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs < 10 ? '0' + secs : secs}`;
};

const formatDistanceToNow = (date: Date) => {
  return formatDistance(date, new Date(), { addSuffix: true });
};

interface QuizHistory {
  id: string;
  game_id: string;
  user_id: string;
  score: number;
  started_at: string;
  completed_at: string;
  time_spent: number;
  correct_answers: number;
  total_questions: number;
  game: {
    id: string;
    title: string;
    description: string;
    created_at: string;
    thumbnail: string;
  }
}

const QuizHistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  // const { session } = useAuthStore();
  
  const [history, setHistory] = useState<QuizHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Hardcode values
  const bgColor = 'white';
  const borderColor = 'gray.200';
  
  useEffect(() => {
    const fetchQuizHistory = async () => {
      if (!user?.id) {
        // Tạm thời bỏ thông báo lỗi khi chưa đăng nhập
        // setError(t('quiz.needLogin'));
        // setLoading(false);
        // return;
        
        // Thay vào đó, hiển thị một thông báo nhỏ nhưng vẫn cho phép sử dụng
        console.log('Anonymous user accessing quiz history');
        setLoading(false);
        // Hiển thị danh sách rỗng thay vì lỗi
        setHistory([]);
        return;
      }
      
      try {
        console.log('Fetching quiz history for user:', user.id);
        const { data, error } = await supabase
          .from('quiz_user_progress')
          .select('*, game:game_id(*)')
          .eq('user_id', user.id)
          .not('completed_at', 'is', null) // Sửa lại cú pháp is.not.null
          .order('completed_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching quiz history:', error);
          setError(t('common.fetchError'));
        } else {
          console.log('Quiz history data:', data);
          setHistory(data || []);
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        setError(t('common.unexpectedError'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchQuizHistory();
  }, [user, t]);
  
  const getScoreColor = (score: number, total: number) => {
    const percentage = (score / (total * 10)) * 100;
    if (percentage >= 80) return "success";
    if (percentage >= 60) return "success";
    if (percentage >= 40) return "warning"; 
    if (percentage >= 20) return "warning";
    return "destructive";
  };
  
  if (loading) {
    return (
      <div className="container py-8 mx-auto">
        <div className="flex flex-col items-center space-y-4">
          <Spinner size="lg" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container py-8 mx-auto">
        <div className="flex flex-col items-center space-y-4">
          <h2 className="text-destructive text-lg font-semibold">{error}</h2>
          <Button onClick={() => navigate('/quiz')}>
            {t('common.backToQuiz')}
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container py-8 mx-auto">
      <div className="flex flex-col space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">{t('quiz.historyTitle')}</h1>
          <Button 
            variant="default"
            onClick={() => navigate('/quiz')}
          >
            {t('quiz.browseGames')}
          </Button>
        </div>
        
        {history.length === 0 ? (
          <Card className="p-6">
            <div className="flex flex-col items-center space-y-4">
              <FaGamepad size={40} className="text-muted-foreground" />
              <p className="text-lg">{t('quiz.noHistory')}</p>
              <Button 
                onClick={() => navigate('/quiz')}
              >
                {t('quiz.startPlaying')}
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {history.map((item) => {
              // Xác định tổng số câu hỏi 
              const totalQuestions = item.total_questions || 10; // Fallback to 10 if not available
              // Xác định số câu trả lời đúng
              const correctAnswers = item.correct_answers || Math.floor(item.score / 10); // Fallback based on score
              
              return (
                <Card 
                  key={item.id} 
                  className={cn(
                    "hover:translate-y-[-4px] transition-transform cursor-pointer shadow-sm hover:shadow-md"
                  )}
                  onClick={() => navigate(`/quiz/results/${item.id}`)}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg line-clamp-1">
                      {item.game?.title || t('quiz.unknownQuiz')}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="flex flex-col space-y-3">
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {item.game?.description || t('quiz.noDescription')}
                      </p>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center space-x-2">
                          <FaTrophy className={`text-${getScoreColor(item.score, totalQuestions)}`} />
                          <span className="font-bold">
                            {item.score}/{totalQuestions * 10}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <FaCheckCircle className="text-green-500" />
                          <span>
                            {correctAnswers}/{totalQuestions}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <FaClock />
                          <span>
                            {formatDuration(item.time_spent || 0)}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <FaCalendarAlt />
                          <span>
                            {formatDistanceToNow(new Date(item.completed_at))}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center pt-2">
                        <Badge variant={getScoreColor(item.score, totalQuestions) as "default" | "secondary" | "destructive" | "outline" | "success" | "warning"}>
                          {item.score >= (totalQuestions * 8) ? t('quiz.excellent') : 
                           item.score >= (totalQuestions * 6) ? t('quiz.good') : 
                           item.score >= (totalQuestions * 4) ? t('quiz.fair') : 
                           t('quiz.needsImprovement')}
                        </Badge>
                        
                        <Button 
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/quiz/results/${item.id}`);
                          }}
                        >
                          <FaEye className="mr-2" />
                          {t('quiz.viewDetails')}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizHistoryPage; 